import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertTriangle,
  Brain,
  Clock,
  Star,
  User,
  ArrowRight,
  Loader2,
  Sparkles,
  RefreshCw,
  ChevronRight,
  Flame,
  Target,
  Zap
} from 'lucide-react';

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200'
};

const PRIORITY_ICONS = {
  high: <Flame className="w-3 h-3" />,
  medium: <Zap className="w-3 h-3" />,
  low: <Target className="w-3 h-3" />
};

export default function AdminAIInsights() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [flaggedData, setFlaggedData] = useState(null);
  const [activeTab, setActiveTab] = useState('highIntensityFears');
  
  useEffect(() => {
    fetchFlaggedParticipants();
  }, []);
  
  const fetchFlaggedParticipants = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get('/api/admin/ai/flagged-participants');
      setFlaggedData(response.data);
    } catch (error) {
      toast.error('Failed to load flagged participants');
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const viewParticipant = (userId) => {
    navigate(`/admin/participants/${userId}`);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="admin-ai-insights-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  const categories = [
    {
      key: 'highIntensityFears',
      label: 'High Intensity',
      icon: <AlertTriangle className="w-4 h-4" />,
      description: 'Participants with acute pressure points requiring attention'
    },
    {
      key: 'significantGaps',
      label: 'Archetype Gaps',
      icon: <Brain className="w-4 h-4" />,
      description: 'Large difference between stated and core archetypes'
    },
    {
      key: 'recentCompletions',
      label: 'Recent Completions',
      icon: <Clock className="w-4 h-4" />,
      description: 'Recently completed assessments ready for review'
    },
    {
      key: 'recommendedFollowUps',
      label: 'Follow-ups',
      icon: <Star className="w-4 h-4" />,
      description: 'AI-recommended priority follow-ups'
    }
  ];
  
  return (
    <div data-testid="admin-ai-insights-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl font-semibold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            AI Insights
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            AI-powered participant analysis and recommendations
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchFlaggedParticipants}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      {/* Stats Overview */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        {categories.map(cat => (
          <Card 
            key={cat.key}
            className={`cursor-pointer transition-all hover:border-primary/50 ${
              activeTab === cat.key ? 'border-primary bg-primary/5' : ''
            }`}
            onClick={() => setActiveTab(cat.key)}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{cat.label}</p>
                  <p className="text-2xl font-bold">
                    {flaggedData?.[cat.key]?.length || 0}
                  </p>
                </div>
                <div className={`p-2 rounded-full ${activeTab === cat.key ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  {cat.icon}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {categories.find(c => c.key === activeTab)?.icon}
            {categories.find(c => c.key === activeTab)?.label}
          </CardTitle>
          <CardDescription>
            {categories.find(c => c.key === activeTab)?.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!flaggedData?.[activeTab]?.length ? (
            <div className="text-center py-12 text-muted-foreground">
              <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No participants in this category</p>
            </div>
          ) : (
            <div className="space-y-4">
              {flaggedData[activeTab].map((participant, index) => (
                <div
                  key={participant.userId || index}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {participant.firstName} {participant.lastName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {participant.email}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {participant.priority && (
                      <Badge className={PRIORITY_COLORS[participant.priority]}>
                        {PRIORITY_ICONS[participant.priority]}
                        <span className="ml-1 capitalize">{participant.priority}</span>
                      </Badge>
                    )}
                    
                    {participant.reason && (
                      <span className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {participant.reason}
                      </span>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => viewParticipant(participant.userId)}
                    >
                      View
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
